import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _createClass from "@babel/runtime/helpers/createClass";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _wrapNativeSuper from "@babel/runtime/helpers/wrapNativeSuper";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function _createSuper(t) { var r = _isNativeReflectConstruct(); return function () { var e, o = _getPrototypeOf(t); if (r) { var s = _getPrototypeOf(this).constructor; e = Reflect.construct(o, arguments, s); } else e = o.apply(this, arguments); return _possibleConstructorReturn(this, e); }; }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useState, useEffect, useRef } from 'react';
import deepEqual from 'fast-deep-equal';
import { iframeResizer } from 'iframe-resizer';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { getGlobalTheme } from '@atlaskit/tokens';
import { withMetricsAndAnalytics } from '../metrics';
import { reload as _reload } from './methods/reload';
import { emit as _emit, on as _on } from './methods/events';
import { captureAndReportError, useTracingContext } from '../../error-reporting';
import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { FORGE_UI_ANALYTICS_CHANNEL } from '../../analytics';
import { useMetricsContext } from '../../context';
var createIframeBridge = null;
// bridge-core import relies on window object. It is not available in SSR.
if (typeof window !== 'undefined') {
  createIframeBridge = require('@atlassian/bridge-core').createIframeBridge;
}

// Type of these methods could be shared with @forge/bridge

var mapCustomBridgeMethods = function mapCustomBridgeMethods(customBridgeMethods) {
  if (!customBridgeMethods) {
    return {};
  }
  return Object.keys(customBridgeMethods).reduce(function (acc, key) {
    return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, key, function (payload) {
      return customBridgeMethods[key](payload === null || payload === void 0 ? void 0 : payload.data);
    }));
  }, {});
};

/**
 * BridgeClientError represents an error that will reject to the user.
 * It is a "normal" error that should not be included in our error metrics/tracking.
 */
export var BridgeClientError = /*#__PURE__*/function (_Error) {
  _inherits(BridgeClientError, _Error);
  var _super = _createSuper(BridgeClientError);
  function BridgeClientError(cause) {
    _classCallCheck(this, BridgeClientError);
    return _super.call(this, cause);
  }
  return _createClass(BridgeClientError);
}( /*#__PURE__*/_wrapNativeSuper(Error));
export var useBridge = function useBridge(_ref) {
  var _extension$appOwner;
  var theme = _ref.theme,
    surfaceColor = _ref.surfaceColor,
    origin = _ref.origin,
    api = _ref.api,
    customBridgeMethods = _ref.customBridgeMethods,
    _onLoad = _ref.onLoad,
    isResizable = _ref.isResizable,
    height = _ref.height,
    environment = _ref.environment,
    extension = _ref.extension,
    iframeRef = _ref.iframeRef;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    loadedAt = _useState4[0],
    setLoadedAt = _useState4[1];
  var _useMetricsContext = useMetricsContext(),
    page = _useMetricsContext.page;
  // This ref avoids the api object ending up in the dep array
  // of the effect that calls createIframeBridge. It means that
  // people can pass anonymous functions as the api without the
  // bridge being re-created every render.
  var apiRef = useRef(api);
  useEffect(function () {
    apiRef.current = api;
  }, [api]);
  var enableThemingRef = useRef();
  var setThemeRef = useRef();
  useEffect(function () {
    if (!setThemeRef.current) {
      return;
    }
    setThemeRef.current(theme, surfaceColor, environment);
  }, [theme, surfaceColor, loading, environment]);

  // same as above, but for customBridgeMethods
  var customBridgeMethodsRef = useRef(customBridgeMethods);
  useEffect(function () {
    customBridgeMethodsRef.current = customBridgeMethods;
  }, [customBridgeMethods]);
  var _useState5 = useState({
      properties: extension.properties,
      type: extension.type,
      environmentType: extension.environmentType,
      appOwnerAccountId: (_extension$appOwner = extension.appOwner) === null || _extension$appOwner === void 0 ? void 0 : _extension$appOwner.accountId
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    errorExtensionDetails = _useState6[0],
    setErrorExtensionDetails = _useState6[1];
  var tracing = useTracingContext();
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  useEffect(function () {
    var _extension$appOwner2;
    var type = errorExtensionDetails.type,
      environmentType = errorExtensionDetails.environmentType,
      properties = errorExtensionDetails.properties,
      appOwnerAccountId = errorExtensionDetails.appOwnerAccountId;
    if (extension.type !== type || extension.environmentType !== environmentType || ((_extension$appOwner2 = extension.appOwner) === null || _extension$appOwner2 === void 0 ? void 0 : _extension$appOwner2.accountId) !== appOwnerAccountId || !deepEqual(extension.properties, properties)) {
      var _extension$appOwner3;
      setErrorExtensionDetails({
        properties: extension.properties,
        type: extension.type,
        environmentType: extension.environmentType,
        appOwnerAccountId: (_extension$appOwner3 = extension.appOwner) === null || _extension$appOwner3 === void 0 ? void 0 : _extension$appOwner3.accountId
      });
    }
  }, [extension, errorExtensionDetails]);
  var id = extension.id,
    installationId = extension.installationId;
  useEffect(function () {
    //this should never happen on client side
    if (!createIframeBridge) {
      return;
    }
    var iframeEl = iframeRef.current;
    if (iframeEl !== null && iframeEl !== void 0 && iframeEl.contentWindow && !loading && typeof loadedAt === 'number') {
      var eventCallbacks = [];
      var extensionDetails = {
        id: id.split('/').slice(0, 3).join('/'),
        installationId: installationId
      };
      var sendHandshakeMetric = function sendHandshakeMetric(page) {
        return function (success, error) {
          if (success) {
            createAnalyticsEvent({
              eventType: OPERATIONAL_EVENT_TYPE,
              data: {
                action: 'succeeded',
                actionSubject: 'forge.ui.bridge',
                attributes: {
                  target: 'handshake'
                },
                source: page,
                tags: ['forge']
              }
            }).fire(FORGE_UI_ANALYTICS_CHANNEL);
          } else {
            createAnalyticsEvent({
              eventType: OPERATIONAL_EVENT_TYPE,
              data: {
                action: 'failed',
                actionSubject: 'forge.ui.bridge',
                attributes: {
                  target: 'handshake'
                },
                source: page,
                tags: ['forge']
              }
            }).fire(FORGE_UI_ANALYTICS_CHANNEL);
            var handshakeError = error instanceof Error ? new Error("Bridge handshake error: ".concat(error.message)) : new Error('Unknown bridge handshake error');
            captureAndReportError({
              error: handshakeError,
              environment: environment,
              errorExtensionDetails: errorExtensionDetails,
              page: page,
              tracing: tracing
            });
          }
        };
      };
      var _createIframeBridge = createIframeBridge({
          theme: theme,
          surfaceColor: surfaceColor,
          withDomain: origin,
          withWindow: iframeEl.contentWindow,
          metadata: {
            env: environment,
            module: extension.type,
            product: extension.type.split(':')[0]
          },
          features: withMetricsAndAnalytics({
            features: _objectSpread({
              invoke: function () {
                var _invoke = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref2) {
                  var data, _apiRef$current, onInvoke, onThreeLO, result;
                  return _regeneratorRuntime.wrap(function _callee$(_context) {
                    while (1) switch (_context.prev = _context.next) {
                      case 0:
                        data = _ref2.data;
                        _apiRef$current = apiRef.current, onInvoke = _apiRef$current.onInvoke, onThreeLO = _apiRef$current.onThreeLO;
                        _context.next = 4;
                        return onInvoke(data);
                      case 4:
                        result = _context.sent;
                        if (!(result.type === '3lo')) {
                          _context.next = 7;
                          break;
                        }
                        return _context.abrupt("return", new Promise(function (resolve, reject) {
                          onThreeLO(result.authUrl, function () {
                            return onInvoke(data).then(function (res) {
                              if (res.type === 'ok') {
                                resolve(res.response);
                              }
                              // Unexpected case where 3LO consent requested again after consenting
                              reject();
                            }, reject);
                          });
                        }));
                      case 7:
                        if (!(result.type === 'err')) {
                          _context.next = 9;
                          break;
                        }
                        throw new Error(result.message);
                      case 9:
                        return _context.abrupt("return", result.response);
                      case 10:
                      case "end":
                        return _context.stop();
                    }
                  }, _callee);
                }));
                function invoke(_x) {
                  return _invoke.apply(this, arguments);
                }
                return invoke;
              }(),
              navigate: function () {
                var _navigate = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(_ref3) {
                  var data;
                  return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                    while (1) switch (_context2.prev = _context2.next) {
                      case 0:
                        data = _ref3.data;
                        return _context2.abrupt("return", apiRef.current.onNavigate(data));
                      case 2:
                      case "end":
                        return _context2.stop();
                    }
                  }, _callee2);
                }));
                function navigate(_x2) {
                  return _navigate.apply(this, arguments);
                }
                return navigate;
              }(),
              reconcile: function () {
                var _reconcile = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(_ref4) {
                  var data;
                  return _regeneratorRuntime.wrap(function _callee3$(_context3) {
                    while (1) switch (_context3.prev = _context3.next) {
                      case 0:
                        data = _ref4.data;
                        return _context3.abrupt("return", apiRef.current.reconcile(data));
                      case 2:
                      case "end":
                        return _context3.stop();
                    }
                  }, _callee3);
                }));
                function reconcile(_x3) {
                  return _reconcile.apply(this, arguments);
                }
                return reconcile;
              }(),
              onError: function () {
                var _onError = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(_ref5) {
                  var data;
                  return _regeneratorRuntime.wrap(function _callee4$(_context4) {
                    while (1) switch (_context4.prev = _context4.next) {
                      case 0:
                        data = _ref5.data;
                        return _context4.abrupt("return", apiRef.current.onError(data));
                      case 2:
                      case "end":
                        return _context4.stop();
                    }
                  }, _callee4);
                }));
                function onError(_x4) {
                  return _onError.apply(this, arguments);
                }
                return onError;
              }(),
              reload: function () {
                var _reload2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
                  return _regeneratorRuntime.wrap(function _callee5$(_context5) {
                    while (1) switch (_context5.prev = _context5.next) {
                      case 0:
                        _reload();
                      case 1:
                      case "end":
                        return _context5.stop();
                    }
                  }, _callee5);
                }));
                function reload() {
                  return _reload2.apply(this, arguments);
                }
                return reload;
              }(),
              refresh: function () {
                var _refresh = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
                  return _regeneratorRuntime.wrap(function _callee6$(_context6) {
                    while (1) switch (_context6.prev = _context6.next) {
                      case 0:
                        return _context6.abrupt("return", apiRef.current.refresh());
                      case 1:
                      case "end":
                        return _context6.stop();
                    }
                  }, _callee6);
                }));
                function refresh() {
                  return _refresh.apply(this, arguments);
                }
                return refresh;
              }(),
              submit: function () {
                var _submit = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7(payload) {
                  return _regeneratorRuntime.wrap(function _callee7$(_context7) {
                    while (1) switch (_context7.prev = _context7.next) {
                      case 0:
                        return _context7.abrupt("return", apiRef.current.submit(payload));
                      case 1:
                      case "end":
                        return _context7.stop();
                    }
                  }, _callee7);
                }));
                function submit(_x5) {
                  return _submit.apply(this, arguments);
                }
                return submit;
              }(),
              close: function () {
                var _close = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8(payload) {
                  return _regeneratorRuntime.wrap(function _callee8$(_context8) {
                    while (1) switch (_context8.prev = _context8.next) {
                      case 0:
                        return _context8.abrupt("return", apiRef.current.close(payload));
                      case 1:
                      case "end":
                        return _context8.stop();
                    }
                  }, _callee8);
                }));
                function close(_x6) {
                  return _close.apply(this, arguments);
                }
                return close;
              }(),
              getContext: function getContext() {
                return apiRef.current.getContext();
              },
              openModal: function () {
                var _openModal = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9(payload) {
                  return _regeneratorRuntime.wrap(function _callee9$(_context9) {
                    while (1) switch (_context9.prev = _context9.next) {
                      case 0:
                        return _context9.abrupt("return", apiRef.current.openModal(payload));
                      case 1:
                      case "end":
                        return _context9.stop();
                    }
                  }, _callee9);
                }));
                function openModal(_x7) {
                  return _openModal.apply(this, arguments);
                }
                return openModal;
              }(),
              createHistory: function () {
                var _createHistory = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
                  return _regeneratorRuntime.wrap(function _callee10$(_context10) {
                    while (1) switch (_context10.prev = _context10.next) {
                      case 0:
                        return _context10.abrupt("return", apiRef.current.createHistory());
                      case 1:
                      case "end":
                        return _context10.stop();
                    }
                  }, _callee10);
                }));
                function createHistory() {
                  return _createHistory.apply(this, arguments);
                }
                return createHistory;
              }(),
              fetchProduct: function () {
                var _fetchProduct = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11(_ref6) {
                  var data;
                  return _regeneratorRuntime.wrap(function _callee11$(_context11) {
                    while (1) switch (_context11.prev = _context11.next) {
                      case 0:
                        data = _ref6.data;
                        return _context11.abrupt("return", apiRef.current.fetchProduct(data));
                      case 2:
                      case "end":
                        return _context11.stop();
                    }
                  }, _callee11);
                }));
                function fetchProduct(_x8) {
                  return _fetchProduct.apply(this, arguments);
                }
                return fetchProduct;
              }(),
              showFlag: function () {
                var _showFlag = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12(_ref7) {
                  var _data$actions;
                  var data, flagActions;
                  return _regeneratorRuntime.wrap(function _callee12$(_context12) {
                    while (1) switch (_context12.prev = _context12.next) {
                      case 0:
                        data = _ref7.data;
                        flagActions = (_data$actions = data.actions) === null || _data$actions === void 0 ? void 0 : _data$actions.map(function (action) {
                          var onClick = action.onClick;
                          return _objectSpread(_objectSpread({}, action), {}, {
                            onClick: onClick ? function () {
                              return onClick();
                            } : function () {}
                          });
                        });
                        return _context12.abrupt("return", apiRef.current.showFlag(_objectSpread(_objectSpread({}, data), {}, {
                          actions: flagActions
                        })));
                      case 3:
                      case "end":
                        return _context12.stop();
                    }
                  }, _callee12);
                }));
                function showFlag(_x9) {
                  return _showFlag.apply(this, arguments);
                }
                return showFlag;
              }(),
              closeFlag: function () {
                var _closeFlag = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee13(_ref8) {
                  var data;
                  return _regeneratorRuntime.wrap(function _callee13$(_context13) {
                    while (1) switch (_context13.prev = _context13.next) {
                      case 0:
                        data = _ref8.data;
                        return _context13.abrupt("return", apiRef.current.closeFlag(data));
                      case 2:
                      case "end":
                        return _context13.stop();
                    }
                  }, _callee13);
                }));
                function closeFlag(_x10) {
                  return _closeFlag.apply(this, arguments);
                }
                return closeFlag;
              }(),
              on: function () {
                var _on2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee14(payload) {
                  var _payload$data, event, callback, subscription;
                  return _regeneratorRuntime.wrap(function _callee14$(_context14) {
                    while (1) switch (_context14.prev = _context14.next) {
                      case 0:
                        _payload$data = payload.data, event = _payload$data.event, callback = _payload$data.callback;
                        subscription = _on(event, callback, extensionDetails);
                        eventCallbacks.push(subscription);
                        return _context14.abrupt("return", {
                          unsubscribe: function unsubscribe() {
                            eventCallbacks.splice(eventCallbacks.indexOf(subscription), 1);
                            subscription.unsubscribe();
                          }
                        });
                      case 4:
                      case "end":
                        return _context14.stop();
                    }
                  }, _callee14);
                }));
                function on(_x11) {
                  return _on2.apply(this, arguments);
                }
                return on;
              }(),
              emit: function () {
                var _emit2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee15(payload) {
                  return _regeneratorRuntime.wrap(function _callee15$(_context15) {
                    while (1) switch (_context15.prev = _context15.next) {
                      case 0:
                        return _context15.abrupt("return", _emit(payload.data.event, payload.data.payload, extensionDetails));
                      case 1:
                      case "end":
                        return _context15.stop();
                    }
                  }, _callee15);
                }));
                function emit(_x12) {
                  return _emit2.apply(this, arguments);
                }
                return emit;
              }(),
              enableTheming: function () {
                var _enableTheming = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee16() {
                  var _enableThemingRef$cur, _setThemeRef$current;
                  var currentTheme, surfaceColor;
                  return _regeneratorRuntime.wrap(function _callee16$(_context16) {
                    while (1) switch (_context16.prev = _context16.next) {
                      case 0:
                        _context16.next = 2;
                        return (_enableThemingRef$cur = enableThemingRef.current) === null || _enableThemingRef$cur === void 0 ? void 0 : _enableThemingRef$cur.call(enableThemingRef);
                      case 2:
                        currentTheme = getGlobalTheme();
                        surfaceColor = null;
                        (_setThemeRef$current = setThemeRef.current) === null || _setThemeRef$current === void 0 || _setThemeRef$current.call(setThemeRef, currentTheme, surfaceColor, environment);
                      case 5:
                      case "end":
                        return _context16.stop();
                    }
                  }, _callee16);
                }));
                function enableTheming() {
                  return _enableTheming.apply(this, arguments);
                }
                return enableTheming;
              }(),
              changeWindowTitle: function () {
                var _changeWindowTitle = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee17(_ref9) {
                  var data;
                  return _regeneratorRuntime.wrap(function _callee17$(_context17) {
                    while (1) switch (_context17.prev = _context17.next) {
                      case 0:
                        data = _ref9.data;
                        apiRef.current.changeWindowTitle(data);
                      case 2:
                      case "end":
                        return _context17.stop();
                    }
                  }, _callee17);
                }));
                function changeWindowTitle(_x13) {
                  return _changeWindowTitle.apply(this, arguments);
                }
                return changeWindowTitle;
              }(),
              getFrameId: function () {
                var _getFrameId = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee18() {
                  return _regeneratorRuntime.wrap(function _callee18$(_context18) {
                    while (1) switch (_context18.prev = _context18.next) {
                      case 0:
                        return _context18.abrupt("return", apiRef.current.getFrameId());
                      case 1:
                      case "end":
                        return _context18.stop();
                    }
                  }, _callee18);
                }));
                function getFrameId() {
                  return _getFrameId.apply(this, arguments);
                }
                return getFrameId;
              }()
            }, mapCustomBridgeMethods(customBridgeMethodsRef.current)),
            page: page,
            environment: environment,
            errorExtensionDetails: errorExtensionDetails,
            createAnalyticsEvent: createAnalyticsEvent,
            tracing: tracing
          }),
          sendHandshakeMetric: sendHandshakeMetric(page)
        }),
        open = _createIframeBridge.open;
      if (isResizable) {
        iframeResizer({
          heightCalculationMethod: 'bodyScroll',
          widthCalculationMethod: 'bodyScroll',
          scrolling: true,
          // @ts-ignore minHeight can be a string
          minHeight: height,
          sizeWidth: extension.properties.layout === 'inline' && extension.type === 'xen:macro',
          // sizeWidth is enabled for resizing only for inline macros
          initCallback: function initCallback(iframe) {
            var _iframe$iFrameResizer;
            iframe === null || iframe === void 0 || (_iframe$iFrameResizer = iframe.iFrameResizer) === null || _iframe$iFrameResizer === void 0 || _iframe$iFrameResizer.resize();
          }
        }, iframeEl);
      }
      var _open = open(),
        close = _open.close,
        enableTheming = _open.enableTheming,
        setTheme = _open.setTheme;
      setThemeRef.current = setTheme;
      enableThemingRef.current = enableTheming;
      return function () {
        close === null || close === void 0 || close(); // close may be undefined here if createIframeBridge call fails
        for (var _i = 0, _eventCallbacks = eventCallbacks; _i < _eventCallbacks.length; _i++) {
          var subscription = _eventCallbacks[_i];
          subscription.unsubscribe();
        }
        (iframeEl === null || iframeEl === void 0 ? void 0 : iframeEl.iFrameResizer) && (iframeEl === null || iframeEl === void 0 ? void 0 : iframeEl.iFrameResizer.removeListeners());
      };
    }
  }, [iframeRef, origin, loading, page, isResizable, height, environment, errorExtensionDetails, id, extension.properties.layout, extension.type, installationId, tracing, theme, surfaceColor, createAnalyticsEvent, loadedAt]);
  return {
    loading: loading,
    iframeProps: {
      ref: iframeRef,
      onLoad: function onLoad() {
        _onLoad && _onLoad();
        setLoading(false);
        setLoadedAt(Date.now());
      }
    }
  };
};